import { useEffect, useState } from "react";
import {
  paletteFamilies,
  readPaletteFamily,
  typographyRoles,
  type PaletteSwatch,
} from "@/lib/palette";
import { PanelBadge } from "./PanelUI";

interface FamilyWithSwatches {
  family: (typeof paletteFamilies)[number];
  swatches: PaletteSwatch[];
}

export default function PaletteTypography() {
  const [families, setFamilies] = useState<FamilyWithSwatches[]>([]);

  useEffect(() => {
    setFamilies(
      paletteFamilies.map((family) => ({
        family,
        swatches: readPaletteFamily(family.key),
      })),
    );
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
          <i className="ri-palette-line text-base text-primary-600" />
        </span>
        <p className="text-xs leading-relaxed text-foreground-600">
          As cores são lidas das variáveis CSS reais do projeto. Se uma cor do site for alterada,
          esta ficha mostra automaticamente o novo valor. A edição da paleta será habilitada
          posteriormente no editor completo.
        </p>
      </div>

      {families.map(({ family, swatches }) => (
        <section key={family.key} className="rounded-lg border border-background-200 bg-background-100 p-4">
          <header className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="font-heading text-xl text-foreground-900">{family.label}</h3>
              <span className="text-xs text-foreground-500">{family.description}</span>
            </div>
            <PanelBadge tone="primary">var(--{family.key}-*)</PanelBadge>
          </header>

          <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
            <span className="text-foreground-500">Aplicação principal:</span> {family.mainApplication}
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
            {swatches.map((swatch) => {
              const isAnchor = family.anchors.includes(swatch.step);
              return (
                <div
                  key={swatch.token}
                  className={`overflow-hidden rounded-md border bg-background-50 ${
                    isAnchor ? "border-primary-300" : "border-background-200"
                  }`}
                >
                  <div className="h-14 w-full" style={{ backgroundColor: swatch.hex }} />
                  <div className="p-2">
                    <p className="flex items-center gap-1 text-[11px] font-medium text-foreground-800">
                      {swatch.token}
                      {isAnchor ? <i className="ri-star-fill text-[9px] text-primary-500" /> : null}
                    </p>
                    <p className="mt-0.5 text-[10px] uppercase text-foreground-500">{swatch.hex}</p>
                    <p className="text-[10px] text-foreground-500">{swatch.rgb}</p>
                    <p className="text-[10px] text-foreground-400">{swatch.oklch}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <ul className="mt-3 flex flex-wrap gap-2">
            {family.applications.map((application) => (
              <li
                key={`${family.key}-${application.usage}`}
                className="rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600"
              >
                <span className="text-foreground-800">{application.usage}</span>
                <span className="text-foreground-400"> · </span>
                {application.token}
              </li>
            ))}
          </ul>
        </section>
      ))}

      <section className="rounded-lg border border-background-200 bg-background-100 p-4">
        <h3 className="font-heading text-xl text-foreground-900">Tipografia</h3>
        <p className="mt-1.5 text-xs text-foreground-600">
          Fontes carregadas pelo projeto e suas aplicações principais.
        </p>

        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
          {typographyRoles.map((role) => (
            <article key={role.variable} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">{role.role}</p>
                <code className="rounded bg-background-200 px-2 py-0.5 text-[10px] text-foreground-700">
                  var({role.variable})
                </code>
              </div>
              <p
                className="mt-2 text-lg text-foreground-900"
                style={{ fontFamily: `var(${role.variable})` }}
              >
                {role.family}
              </p>
              <p
                className="mt-2 text-2xl leading-snug text-foreground-800"
                style={{ fontFamily: `var(${role.variable})` }}
              >
                Moda para viver cada versão de você
              </p>
              <p
                className="mt-1 text-sm text-foreground-500"
                style={{ fontFamily: `var(${role.variable})` }}
              >
                ABCDEFGHIJKLM · NOPQRSTUVWXYZ · 0123456789
              </p>
              <p className="mt-3 text-[11px] text-foreground-600">
                <span className="text-foreground-500">Pesos usados:</span> {role.weights}
              </p>
              <p className="mt-1 text-[11px] text-foreground-600">{role.usage}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}